// Shared helpers for the Money pages (FINANCE-001, owner 2026-10-22): which ledger accounts are money,
// expense categories, suppliers or income; what kind of movement a journal entry is; account codes for
// accounts the merchant names; and the report periods. Money is integer paisa.
import { useQuery } from "@tanstack/react-query";
import { i18n } from "@ady/i18n";
import type { IconName } from "@ady/ui";
import { LEDGER_ACCOUNT_CODES } from "@ady/contracts";
import { useStoreIds } from "../orders/ui";
import {
  financeApi,
  financeKeys,
  nptDay,
  type AccountBalance,
  type JournalEntry,
  type PostLine,
} from "./api";

const CHART: ReadonlySet<string> = new Set(LEDGER_ACCOUNT_CODES);

/** The fixed chart's accounts use the string layer; custom accounts show the merchant's own name. */
export const accountLabel = (code: string, name: string): string =>
  CHART.has(code)
    ? i18n.tDynamic(`payments.finance.account.${code}`, "payments.finance.account.cash")
    : name;
export const nameOf = (a: Pick<AccountBalance, "code" | "name">) => accountLabel(a.code, a.name);

/** Money the shop holds: cash, bank, wallets, POS drawers, gateway and courier money on its way. */
export const MONEY_KINDS: ReadonlySet<string> = new Set([
  "cash",
  "bank",
  "wallet",
  "drawer",
  "clearing",
  "courier_receivable",
]);
/** Money accounts a person pays from or into by hand (gateway and courier money moves itself). */
const HAND_KINDS: ReadonlySet<string> = new Set(["cash", "bank", "wallet", "drawer"]);

/** Assets, expenses and contra revenue grow with debits; the rest with credits (ledger.ts). */
const DEBIT_NORMAL: ReadonlySet<string> = new Set([
  ...MONEY_KINDS,
  "cogs",
  "expense",
  "contra_revenue",
]);
export const isDebitNormal = (kind: string) => DEBIT_NORMAL.has(kind);
export const isMoney = (a: Pick<AccountBalance, "kind">) => MONEY_KINDS.has(a.kind);
export const isHandMoney = (a: Pick<AccountBalance, "kind">) => HAND_KINDS.has(a.kind);
export const isExpense = (a: Pick<AccountBalance, "kind">) =>
  a.kind === "expense" || a.kind === "cogs";
export const isSupplier = (a: Pick<AccountBalance, "kind" | "system">) =>
  a.kind === "payable" && !a.system;
export const isIncome = (a: Pick<AccountBalance, "kind">) => a.kind === "revenue";
export const isOwner = (a: Pick<AccountBalance, "kind">) => a.kind === "equity";
export const isActive = (a: Pick<AccountBalance, "active">) => a.active !== false;

export const KIND_ICON: Record<string, IconName> = {
  cash: "money",
  bank: "building",
  wallet: "wallet",
  drawer: "drawer",
  clearing: "card",
  courier_receivable: "truck",
  expense: "receipt",
  cogs: "box",
  payable: "users",
  revenue: "trending",
  contra_revenue: "refresh",
  equity: "users",
  liability: "file",
  tax: "file",
};

/** A posting line for this account: custom accounts by ID, chart accounts by code (a new store's
 *  chart has no IDs until its first posting). */
export function line(a: AccountBalance, side: "debit" | "credit", paisa: number): PostLine {
  const ref = a.id && !a.system ? { account_id: a.id } : { account: a.code };
  return side === "debit" ? { ...ref, debit_paisa: paisa } : { ...ref, credit_paisa: paisa };
}

/** A code for an account the merchant names (`exp_shop_rent_k3f9`); the server requires
 *  ^[a-z][a-z0-9_]{2,30}$ and unique per store, so a short random tail avoids clashes. */
export function newCode(prefix: "acc" | "exp" | "sup" | "inc" | "own", name: string): string {
  const slug = name
    .normalize("NFKD")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 16);
  const tail = [...crypto.getRandomValues(new Uint8Array(4))]
    .map((b) => "abcdefghijkmnpqrstuvwxyz23456789"[b % 32])
    .join("");
  return [prefix, slug, tail].filter(Boolean).join("_").slice(0, 31);
}

// ---------------------------------------------------------------- entry kinds

export type EntryKind =
  | "in"
  | "out"
  | "transfer"
  | "expense"
  | "bill"
  | "supplierPayment"
  | "sale"
  | "refund"
  | "cod"
  | "settlement"
  | "reversal"
  | "other";

export const ENTRY_KIND_ICON: Record<EntryKind, IconName> = {
  in: "download",
  out: "upload",
  transfer: "swap",
  expense: "receipt",
  bill: "file",
  supplierPayment: "users",
  sale: "cart",
  refund: "undo",
  cod: "truck",
  settlement: "card",
  reversal: "undo",
  other: "list",
};
export const ENTRY_KIND_TONE: Record<EntryKind, string> = {
  in: "bg-success-bg text-success-fg",
  sale: "bg-success-bg text-success-fg",
  cod: "bg-success-bg text-success-fg",
  settlement: "bg-selected text-link",
  out: "bg-danger-bg text-danger-fg",
  expense: "bg-danger-bg text-danger-fg",
  refund: "bg-danger-bg text-danger-fg",
  supplierPayment: "bg-warning-bg text-warning-fg",
  bill: "bg-warning-bg text-warning-fg",
  transfer: "bg-selected text-link",
  reversal: "bg-sunken text-fg-secondary",
  other: "bg-sunken text-fg-secondary",
};

/** What a journal entry is, from its source and the kinds of the accounts it touches. */
export function entryKind(
  e: JournalEntry,
  kindOf: (code: string) => string | undefined,
): EntryKind {
  if (e.reversal_of) return "reversal";
  const by: Record<string, EntryKind> = {
    payment: "sale",
    pos_shift: "sale",
    order: "sale",
    refund: "refund",
    cod_remittance: "cod",
    settlement: "settlement",
  };
  if (by[e.source_type]) return by[e.source_type]!;
  const kinds = e.lines.map((l) => ({ k: kindOf(l.account) ?? "", d: l.debit_paisa > 0 }));
  const money = (k: string) => MONEY_KINDS.has(k);
  const supplier = (k: string) => k === "payable";
  if (kinds.every((x) => money(x.k))) return "transfer";
  if (kinds.some((x) => supplier(x.k) && !x.d)) return "bill";
  if (kinds.some((x) => supplier(x.k) && x.d)) return "supplierPayment";
  if (kinds.some((x) => (x.k === "expense" || x.k === "cogs") && x.d)) return "expense";
  if (kinds.some((x) => money(x.k) && x.d)) return "in";
  if (kinds.some((x) => money(x.k) && !x.d)) return "out";
  return "other";
}

export const entryTotal = (e: Pick<JournalEntry, "lines">) =>
  e.lines.reduce((x, l) => x + l.debit_paisa, 0);

// ---------------------------------------------------------------- periods

export const PERIODS = ["thisMonth", "lastMonth", "d30", "d90", "d365"] as const;
export type Period = (typeof PERIODS)[number];

/** [from, to] as NPT days (YYYY-MM-DD, both included) for a period preset. */
export function periodRange(p: Period, now = Date.now()): { from: string; to: string } {
  const today = nptDay(0, now);
  const [y, m] = today.split("-").map(Number) as [number, number];
  const iso = (yy: number, mm: number, dd: number) =>
    new Date(Date.UTC(yy, mm - 1, dd)).toISOString().slice(0, 10);
  switch (p) {
    case "thisMonth":
      return { from: iso(y, m, 1), to: today };
    case "lastMonth":
      return { from: iso(y, m - 1, 1), to: iso(y, m, 0) };
    case "d30":
      return { from: nptDay(29, now), to: today };
    case "d90":
      return { from: nptDay(89, now), to: today };
    case "d365":
      return { from: nptDay(364, now), to: today };
  }
}

/** A preset, all time ("lifetime") or dates picked on the calendar ("custom"). */
export type Span = Period | "lifetime" | "custom";
/** The first day "Lifetime" covers: older than any store. */
export const LIFETIME_FROM = "2000-01-01";

/** [from, to] for a span; "custom" uses the picked dates. */
export function spanRange(
  s: Span,
  custom: { from: string; to: string },
  now = Date.now(),
): { from: string; to: string } {
  if (s === "lifetime") return { from: LIFETIME_FROM, to: nptDay(0, now) };
  if (s === "custom") return custom;
  return periodRange(s, now);
}

/** The store's accounts with balances (shared by every Money page and popup). */
export function useAccounts(enabled = true) {
  const { orgId, storeId } = useStoreIds();
  return useQuery({
    queryKey: financeKeys.accounts(orgId, storeId),
    queryFn: () => financeApi.accounts(orgId, storeId),
    enabled,
  });
}
